export enum TrackingEvent {
  DEFAULT = 'default',
  VIRTUAL_PAGEVIEW = 'VirtualPageview'
}

export enum PageViewType {
  SYSTEM_INITIATED = 'system_initiated',
  USER_INITIATED = 'user_initiated'
}

export enum RegistrationStatus {
  COMPLETE = 'complete',
  INITIATED = 'initiated'
}

/* eslint-disable @typescript-eslint/naming-convention */
export enum EcommerceDataKeys {
  EECaddToCart = 'add',
  EECproductImpression = 'impressions',
  EECpurchase = 'purchase',
  EECremoveFromCart = 'remove'
}
/* eslint-enable @typescript-eslint/naming-convention */

export enum PromotionDataKeys {
  EEC_PROMOTION_CLICK = 'EECpromotionClick',
  EEC_PROMOTION_IMPRESSION = 'EECpromotionImpression'
}

/* eslint-disable @typescript-eslint/naming-convention */
export enum PromoViewClickKey {
  EECpromotionClick = 'promoClick',
  EECpromotionImpression = 'promoView'
}
/* eslint-enable @typescript-eslint/naming-convention */

export const PAGE_STRUCTURE_ANGULAR = 'Angular';

interface GenericTrackingData {
  [key: string]: any;
}

export type TrackingData = GenericTrackingData & TrackingContentData & TrackingUserData & TrackingProductData;

export interface TrackingPageViewData extends TrackingData {
  event: string;
  virtualPageURL: string;
}

export interface TrackingEventData extends TrackingData {
  event: string;
  eventCategory?: string;
  eventAction?: string;
  eventLabel?: string;
  eventValue?: any;
}

export interface TrackingEcommerceEventData extends TrackingData {
  event: keyof typeof EcommerceDataKeys;
  currencyCode: string;
  products: GenericTrackingData[];
}

export interface TrackingPromotionEventData extends TrackingData {
  event: PromotionDataKeys;
  ecommerce: {
    [key in PromoViewClickKey]?: {
      promotions: TrackingPromotionData[];
    };
  };
}

/**
 * AnalyticsEvent is to be used when calling AnalyticsService.sendEvent method.
 * It contains shorthand names for `TrackingEventData` props for easier writing.
 */
export interface AnalyticsEvent extends TrackingData {
  event?: string;
  category?: string;
  action?: string;
  label?: string;
  value?: any;
}

/**
 * Below interface definitions are base on DPI tracking property list
 * provided in https://jira.hilti.com/browse/HDP-55095
 *
 * The interfaces are split by Variable Category
 */

export interface TrackingContentData {
  region?: string;
  country?: string;
  currencyCodeMO?: string;
  pageLanguage?: string;
  pageName?: string;
  templateType?: string;
  previousTemplateType?: string;
  previousPagePath?: string;
  pageStructure?: string;
  tabInfo?: string;
  pageviewType?: PageViewType;
  menuL1?: string;
  menuL2?: string;
  menuL3?: string;
  menuL4?: string;
  topic?: string;
  subTopic?: string;
  subSubTopic?: string;
  intention?: string;
  targetPersona?: string;
  mobileApp?: boolean;
  funnelName?: string;
  funnelStepNumber?: string;
  funnelStepName?: string;
  searchType?: string;
  searchTermTyped?: string;
  searchTermClicked?: string;
  searchResultsVolume?: string;
  navigationItem?: string;
  navigationItemDetails?: string;
  previousPageName?: string;
  // was not part of DPI list but likely to be added as it is part of Hybris tracking data
  webSiteType?: string;
}

export interface TrackingUserData {
  hiltiSession?: boolean;
  hiltiUser?: boolean;
  hiltiDevice?: string;
  loggedIn?: boolean;
  registrationStatus?: RegistrationStatus;
  account?: string | number;
  cloudID?: string;
  contact_id?: string;
  fleetContract?: boolean;
  jobFunction?: string;
  potentialClass?: string;
  recProdGroup?: string;
  trade?: string;
  focusCustomer?: boolean;
  punchout?: boolean;
}

export interface TrackingProductData {
  cartNotEmpty?: boolean;
  productItemId?: string;
  productRangeId?: string;
  productSKU?: string;
  productCategory?: string;
  productName?: string;
  productKit?: boolean;
  productPreconfiguredBox?: boolean;
  productPurchaseType?: string;
  productBrand?: string;
  cartPurchaseType?: string;
  cartBrand?: string;
  cartQuantity?: string;
  cartRangeId?: string;
  cartCategory?: string;
  cartSKU?: string;
  cartValue?: string;
  cartDelivery?: string;
  cartDeliveryDetails?: string;
}

export interface TrackingPromotionData {
  id: string;
  name: string;
  creative: string;
  position?: string | number;
}
